import React, { ReactNode } from 'react';
import { moment, i18n } from '@international/mastodon-i18n';
import T from 'components/T';
import { startCase, camelCase } from 'lodash';
import { DocumentStatusParams, DocumentStatus } from './status';
import {
  DateObject,
  DocumentType,
  ExtendedFile,
  FieldConfiguration,
  SlotPins,
} from './interfaces';
import { emptyDocumentOption, multiDocuments } from './constants';

// EVERI-4005: top-level VERIFICATION_FORM_NAME field for frontend-only pins; see stripDocumentSlotPins.
export const SLOT_PINS_FIELD = 'multidocSlotPins';

type DocumentMap = Record<string, Record<string, any[]>>;
type GetDocumentsFn = (field: string) => any;
type SetDocumentsFn = (updater: (prev: DateObject[]) => DateObject[]) => void;
type UpdateDocumentsFn = (exceptionId: string, documents: any) => void;

const CANDIDATE_MULTI_DOCUMENTS_DISCLOSURE_VERSION = '2025-05-08';

// Formats a date string to 'MM/YYYY' or returns 'now' if the date is not provided
const formatDate = (date: string) =>
  date ? moment.utc(date).format('MM/YYYY') : i18n.getStr('labels.now');

// Formats start and end dates
const formatDates = (startDate: string, endDate: string) => {
  const start = startDate ? moment.utc(startDate) : null;
  const end = endDate ? moment.utc(endDate) : moment.utc();
  const isYearly = start && end ? start.year() === end.year() : false;

  return {
    startDateFormatted: formatDate(startDate),
    endDateFormatted: formatDate(endDate),
    isYearly,
  };
};

export const getTranslation = (type: string) => {
  const i18nKey = `components.Verification.EmploymentMultidocPanel.${camelCase(
    type,
  )}`;
  const translated = i18n.getStr(i18nKey);
  return translated !== i18nKey ? translated : startCase(type);
};

const formatDocumentTypes = ({
  type,
  requiredForVerification,
}: DocumentType) => {
  const name = getTranslation(type);
  return {
    value: type,
    name,
    disabled: false,
    uploadId: '',
    requiredForVerification,
  };
};

export const getDocumentOptions = (documentTypes: DocumentType[]) => [
  emptyDocumentOption,
  ...documentTypes.map(formatDocumentTypes),
];

// Oxford "X, Y, or Z" join of required types — interpolated into the
// file-type-not-accepted error title. Reads the RAW `documentTypes`
// (`{type, requiredForVerification}`) and short-circuits empty / single-item
// inputs to avoid dangling punctuation.
export const formatAcceptedTypesList = (documentTypes: DocumentType[]) => {
  const names = (documentTypes || [])
    .filter(doc => doc?.requiredForVerification)
    .map(doc => getTranslation(doc.type));
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} or ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, or ${names[names.length - 1]}`;
};

// Maps field configurations to document requirements
const mapFieldConfigurationsToDocuments = (
  fieldConfigurations: FieldConfiguration[],
) => {
  return fieldConfigurations.map(
    ({ id, requestedDocumentAttributes, type }) => ({
      id,
      year: requestedDocumentAttributes?.year,
      exceptionType: type,
    }),
  );
};

// Returns the instruction key according to the package policy
const readPolicy = (policy: string, isYearly: boolean, isSameYear: boolean) => {
  switch (policy) {
    case 'thorough':
      return 'annualDocument';
    case 'complete':
      if (isYearly) return 'annualDocument';
      return isSameYear ? 'startDateDocument' : 'endDateDocument';
    case 'basic':
    default:
      return 'annualDocument';
  }
};

// Generates initial documents based on field configurations
const generateInitialDocuments = (
  fieldConfigurations: FieldConfiguration[],
) => {
  return mapFieldConfigurationsToDocuments(fieldConfigurations).map(
    ({ id, year, exceptionType }) => {
      return {
        id,
        year,
        value: year,
        name: year,
        disabled: false,
        uploadId: '',
        exceptionType,
      };
    },
  );
};

// Checks if a value is a four-digit year
const isFourDigitYearOrInterval = (value: string) => {
  return /^(\d{4})(\s*-\s*\d{4})?$/.test(value);
};

// Builds the year dropdown options for a single row.
//   - `includeUsedSuffix` labels already-claimed years as "{{year}} (used)"
//     (paired with the existing `disabled: true` flag), except the row's own
//     `currentYear` so it doesn't read "(used)" against itself.
// Defaults off so the non-OCR path keeps its bare-year labels.
const getYearDropdownOptions = (
  years: DateObject[],
  {
    includeUsedSuffix = false,
    currentYear = '',
  }: {
    includeUsedSuffix?: boolean;
    currentYear?: string;
  } = {},
) => {
  const baseYears = [
    ...years.slice(0, 2),
    ...years.slice(2).filter(item => isFourDigitYearOrInterval(item.value)),
  ];

  if (!includeUsedSuffix) return baseYears;

  return baseYears.map(item =>
    item.disabled &&
    item.value !== currentYear &&
    isFourDigitYearOrInterval(item.value)
      ? {
          ...item,
          name: i18n.getStr('components.Verification.AiDocReview.yearUsed', {
            year: item.value,
          }),
        }
      : item,
  );
};

// Checks if a value is an empty string
const isEmpty = (value: string) => {
  return value === '';
};

const getOcrPlaceholder = (selectedDocumentType: any, selectedYear: any) => {
  if (!selectedDocumentType) return '';

  if (isFourDigitYearOrInterval(selectedYear)) {
    return `${getTranslation(selectedDocumentType.value)} for ${selectedYear}`;
  }
  return `${getTranslation(selectedDocumentType.value)}`;
};

const getFileLabel = (
  doc: ExtendedFile,
  status: DocumentStatus,
  company: string,
  fullName: string,
  ocr: any,
  placeholder: string,
): ReactNode => {
  if (!ocr.completed) return doc.filename;
  if (ocr.inEditMode)
    return i18n.getStr('components.Verification.AiDocReview.select');

  if (!DocumentStatusParams[status]?.verified) {
    return (
      <T
        value={`components.Verification.AiDocReview.${status}`}
        company={company}
        fullName={fullName}
        dangerousHTML
      />
    );
  }
  return placeholder;
};

// Updates a state array at a specific index with a new value
const updateStateArray = (
  setState: any,
  key: string,
  value: any,
  index?: number,
) => {
  setState((prevState: any) => {
    const updatedArray = { ...prevState };

    if (index !== undefined) {
      updatedArray[key] = [...updatedArray[key]];
      updatedArray[key][index] = value;
    } else {
      updatedArray[key] = value;
    }
    return updatedArray;
  });
};

// Updates enabled documents based on uploaded IDs
const updateEnabledDocuments = (
  documents: DateObject[],
  uploadedIds: string[],
) => {
  return documents
    .map(doc =>
      doc.disabled && doc.uploadId && !uploadedIds.includes(doc.uploadId)
        ? { ...doc, disabled: false, uploadId: '' }
        : doc,
    )
    .filter(
      // EVERI-4005: a multi_documents entry is only ever pushed alongside a real upload — drop it once that upload is gone, so isDocumentCountSatisfied can't count a withdrawn file. The bucket-anchor entry (uploadId '') is untouched.
      doc =>
        !(
          doc.exceptionType === multiDocuments &&
          doc.uploadId &&
          !uploadedIds.includes(doc.uploadId)
        ),
    );
};

// Normalizes the apply-time "previously provided" block delivered by the BE at
// `metadata.exceptionContext` — the documents the candidate attached on their
// original background check plus the explanation they entered. Returns
// `undefined` when the block is absent or carries neither documents nor an
// explanation, so the panel no-renders gracefully (and for candidates who
// provided nothing at apply time). The report timestamp is pre-formatted here
// for testability ("From your original background check on {date}").
const formatPreviouslyProvided = (metadata: any) => {
  const exceptionContext = metadata?.exceptionContext;
  if (!exceptionContext) return undefined;
  const {
    candidateExplanation = '',
    documents = [],
    reportCreatedAt = '',
  } = exceptionContext;
  const hasDocuments = Array.isArray(documents) && documents.length > 0;
  if (!hasDocuments && !candidateExplanation) return undefined;
  return {
    candidateExplanation,
    // Map the BE `{ name, type }` shape to the field SimpleDocumentList renders.
    // filename is displayed as-is (clean name is persisted; no S3 prefix here).
    supportingDocuments: hasDocuments
      ? documents.map((doc: { name: string; type?: string }) => ({
          filename: doc.name,
          type: doc.type,
        }))
      : [],
    originalReportDateFormatted: reportCreatedAt
      ? moment.utc(reportCreatedAt).format('MMMM D, YYYY')
      : '',
  };
};

// Extracts document requirements from verification data
const extractDocumentRequirements = (verification: any) => {
  const {
    companyLogoUri = '',
    fieldConfigurations = [],
    metadata = {},
    firstName = '',
    lastName = '',
  } = verification;
  const fieldConfiguration = fieldConfigurations[0] || {};
  const requestedAttributes =
    fieldConfiguration.requestedDocumentAttributes || {};
  const previouslyProvided = formatPreviouslyProvided(metadata);
  const screeningId =
    metadata.configuration?.screeningOnResolveActions?.[0]?.screeningId;
  const {
    startDate = '',
    endDate = '',
    position = '',
    hiringPolicy = '',
    company = '',
    type = '',
  } = requestedAttributes;
  const { startDateFormatted, endDateFormatted, isYearly } = formatDates(
    startDate,
    endDate,
  );
  const documentsRequired = generateInitialDocuments(fieldConfigurations);
  const fullName = `${firstName} ${lastName}`;

  return {
    company,
    companyLogoUri,
    documentsRequired,
    endDateFormatted,
    hiringPolicy,
    position,
    startDateFormatted,
    screeningId,
    type,
    fullName,
    isYearly,
    previouslyProvided,
  };
};

// Retrieves documents by exception ID, excluding a specific upload ID
const getDocumentsByExceptionId = (
  getDocuments: any,
  exceptionId: string,
  uploadId: string,
) => {
  const exceptionEntries = getDocuments(exceptionId) ?? {};

  return Object.keys(exceptionEntries).reduce<Record<string, any[]>>(
    (acc, docType) => {
      const entries = Array.isArray(exceptionEntries?.[docType])
        ? exceptionEntries[docType]
        : [];
      acc[docType] = entries.filter((doc: any) => doc.uploadId !== uploadId);
      return acc;
    },
    {},
  );
};

// Splits documents into requested and additional based on conditions
const splitDocuments = (documents: any[], documentsRequired: any[]) => {
  const requestedUploadIdMap = new Map(
    documentsRequired.map(
      ({ type, uploadId, id, exceptionId, requiredForVerification }) => [
        uploadId,
        { type, id: id || exceptionId, requiredForVerification },
      ],
    ),
  );

  return documents.reduce<[any[], any[]]>(
    ([requested, additional], doc) => {
      const exceptionId = requestedUploadIdMap.get(doc.uploadId);
      exceptionId?.requiredForVerification
        ? requested.push({
            ...doc,
            exceptionId: exceptionId.id,
            requiredForVerification: exceptionId?.requiredForVerification,
          })
        : additional.push({
            ...doc,
            exceptionId: exceptionId?.id,
            requiredForVerification: exceptionId?.requiredForVerification,
          });
      return [requested, additional];
    },
    [[], []],
  );
};

// Groups documents into provided, remaining, required, and additional categories
const groupedDocuments = (documents: DateObject[]) => {
  return documents.reduce<{
    documentsProvided: DateObject[];
    remainingDocuments: DateObject[];
    documentsRequired: DateObject[];
    optionalDocuments: DateObject[];
  }>(
    (acc, file) => {
      if (isFourDigitYearOrInterval(file.value)) {
        acc.documentsRequired.push(file);
        if (file.uploadId !== '') {
          acc.documentsProvided.push(file);
        }
        if (!file.disabled) {
          acc.remainingDocuments.push(file);
        }
      }
      if (file.exceptionType === multiDocuments) {
        acc.optionalDocuments.push(file);
      }
      return acc;
    },
    {
      documentsProvided: [],
      remainingDocuments: [],
      documentsRequired: [],
      optionalDocuments: [],
    },
  );
};

// EVERI-4005: "package setting" gate — optionalDocuments always includes the backend's own multi_documents bucket-anchor entry (uploadId ''), so only entries with a real uploadId count as provided.
const isDocumentCountSatisfied = (
  candidateDocContextEnabled: boolean,
  documentsProvided: DateObject[],
  optionalDocuments: DateObject[],
  documentsRequired: DateObject[],
): boolean => {
  if (!candidateDocContextEnabled) return false;
  const uploadedOptionalDocuments = optionalDocuments.filter(
    doc => doc.uploadId !== '',
  );
  return (
    documentsProvided.length + uploadedOptionalDocuments.length >=
    documentsRequired.length
  );
};

// Updates documents when multiple files are uploaded
const uploadedFilesUpdate = (
  uploadedFiles: any[],
  stateYears: DateObject[],
  updateDocuments: (docId: string, documents: Record<string, any[]>) => void,
  getDocuments: (docId: string) => Record<string, any[]>,
) => {
  const multiDocs = uploadedFiles.reduce<DocumentMap>((acc, file) => {
    const matchingDocument = stateYears.find(document => {
      return document.uploadId === file.uploadId && document?.id;
    });
    const exceptionType = matchingDocument?.exceptionType ?? '';
    if (matchingDocument?.id) {
      // Flag a document as manually labeled whenever the candidate selected the
      // type/year by hand (`candidateLabeled`), for every document type. OCR
      // auto-classified values are never flagged. The backend decides relevance —
      // it only reads this flag for QA routing on required-for-verification
      // document types and ignores it on optional/supporting ones.
      const manuallyLabeled = Boolean(matchingDocument.candidateLabeled);
      const candidateContext = matchingDocument.candidateContext?.trim(); // EVERI-4005
      file.metadata = {
        context_attributes: {
          year: matchingDocument?.year,
          candidate_disclosure_version:
            CANDIDATE_MULTI_DOCUMENTS_DISCLOSURE_VERSION,
          upload_id: file.uploadId,
          ...(manuallyLabeled && { manually_labeled: true }),
          ...(candidateContext && { candidate_context: candidateContext }),
        },
        document_class: matchingDocument.type,
      };
      acc[matchingDocument.id] = {
        [exceptionType]: [
          ...(acc[matchingDocument.id]?.[exceptionType] || []),
          file,
        ],
      };
    }
    return acc;
  }, {});

  Object.entries(multiDocs).forEach(([docId, doc]) => {
    const [docType] = Object.keys(doc);
    updateDocuments(docId, {
      [docType]: [...(getDocuments(docId)?.[docType] || []), ...doc[docType]],
    });
  });
};

// Removes partially uploaded documents when the process is canceled
const onCancel = (setShowModal: any, setDocuments: any, uploadedFiles: any) => {
  setShowModal(false);

  const indexesToRemove = uploadedFiles.map((file: any) => file.uploadId);
  setDocuments((prevDocuments: any) =>
    prevDocuments.filter((doc: any) => !indexesToRemove.includes(doc.uploadId)),
  );
};

// Handles form submission
const onSubmit = (setShowModal: any) => {
  setShowModal(false);
};

const createOnRemoveByUploadId = (
  setDocuments: SetDocumentsFn,
  getDocuments: GetDocumentsFn,
  updateDocuments: UpdateDocumentsFn,
) => {
  return (uploadId: string, exceptionId: string) => {
    setDocuments(prevDocuments =>
      prevDocuments.filter(file => file.uploadId !== uploadId),
    );

    const exceptionDocs = getDocumentsByExceptionId(
      getDocuments,
      exceptionId,
      uploadId,
    );

    updateDocuments(exceptionId, exceptionDocs);
  };
};

const allOcrCompleted = (hasOcrInProgress: boolean[]) => {
  return hasOcrInProgress.every(Boolean);
};

const getSortedFileIndices = (
  files: ExtendedFile[],
  statuses: Record<number, DocumentStatus>,
) => {
  return files
    .map((_, i) => i)
    .sort((a, b) => {
      const statusA = statuses[a];
      const statusB = statuses[b];
      const orderA = DocumentStatusParams[statusA]?.sortOrder;
      const orderB = DocumentStatusParams[statusB]?.sortOrder;

      return orderA - orderB;
    });
};

function intervalContainsYear(interval: string, year: string) {
  if (!interval) return false;
  const intervals = interval.split('-').map(v => v.trim());

  if (intervals.length === 1) {
    return intervals[0] === year;
  }

  const [start, end] = intervals.map(Number);
  const target = Number(year);

  return target >= start && target <= end;
}

// EVERI-4005: strips the pins before submit — unconditional, since a flag-gated strip could leave a stale pin behind.
const stripDocumentSlotPins = <T extends Record<string, any>>(data: T): T => {
  if (!data || !(SLOT_PINS_FIELD in data)) return data;
  const { [SLOT_PINS_FIELD]: _pins, ...rest } = data;
  return rest as T;
};

// EVERI-4005: rendering-time join only — never mutates the row or the doc's own context_attributes.year.
const findPinnedDocument = (
  year: string,
  additionalDocuments: any[],
  slotPins: SlotPins = {},
) =>
  additionalDocuments.find(doc => doc?.handle && slotPins[doc.handle] === year);

export {
  allOcrCompleted,
  extractDocumentRequirements,
  updateEnabledDocuments,
  isFourDigitYearOrInterval,
  getYearDropdownOptions,
  isEmpty,
  getDocumentsByExceptionId,
  splitDocuments,
  readPolicy,
  updateStateArray,
  uploadedFilesUpdate,
  groupedDocuments,
  isDocumentCountSatisfied,
  onCancel,
  onSubmit,
  createOnRemoveByUploadId,
  getOcrPlaceholder,
  getFileLabel,
  getSortedFileIndices,
  intervalContainsYear,
  stripDocumentSlotPins,
  findPinnedDocument,
};
